import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Megaphone, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders live promo banners at the top of the search flow once a user has
 * picked a manufacturer. Hidden entirely when there are no active banners.
 */
export default function PromoBannerStrip({ manufacturerId }) {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    if (!manufacturerId) {
      setBanners([]);
      return;
    }
    let cancelled = false;
    api
      .get("/promo-banners/active", { params: { manufacturer_id: manufacturerId } })
      .then(({ data }) => {
        if (!cancelled) setBanners(data || []);
      })
      .catch(() => {
        if (!cancelled) setBanners([]);
      });
    return () => {
      cancelled = true;
    };
  }, [manufacturerId]);

  if (!banners.length) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="promo-banner-strip">
      {banners.map((b) => {
        const href = b.flyer_pdf_id
          ? `${BACKEND_URL}/api/pdfs/${b.flyer_pdf_id}`
          : b.flyer_url || null;
        const inner = (
          <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-sm hover:border-amber-400 transition-colors">
            <div className="w-8 h-8 rounded-sm bg-amber-500 text-white flex items-center justify-center shrink-0">
              <Megaphone className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display font-bold text-sm text-slate-900">
                  {b.title}
                </div>
                {href && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data text-amber-700">
                    <ExternalLink className="w-3 h-3" /> View flyer
                  </span>
                )}
              </div>
              {b.description && (
                <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                  {b.description}
                </div>
              )}
            </div>
          </div>
        );
        return href ? (
          <a
            key={b.id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            data-testid={`promo-banner-${b.id}`}
          >
            {inner}
          </a>
        ) : (
          <div key={b.id} data-testid={`promo-banner-${b.id}`}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}
